import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ExternalLink, Search, X } from 'lucide-react';
import { api, describeError } from '@/lib/api';
import { Badge } from '@/components/ui';
import { CopyButton } from '@/components/CopyButton';
import { subscriptionBadge, subscriptionStatusOptions } from '@/components/badge-labels';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { AcquisitionSummaryCard } from '@/components/AcquisitionSummaryCard';
import { sourceFilterLabel, type TenantAcquisition } from '@/lib/acquisition';

interface TenantListItem {
  id: string;
  tradeName: string;
  ownerName: string;
  slug: string;
  phone: string;
  isActive: boolean;
  subscriptionStatus: string;
  trialExpiresAt: string | null;
  createdAt: string;
  /** URL canônica do cardápio, calculada no servidor. */
  menuUrl: string;
  /** Origem de campanha do cadastro; null = acesso direto. */
  acquisition: TenantAcquisition | null;
}

interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos' },
  ...subscriptionStatusOptions(['Trial', 'Active', 'PastDue', 'Cancelled', 'Expired']),
];

export function TenantsListPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<TenantListItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [isActive, setIsActive] = useState<'true' | 'false' | ''>('');
  const [q, setQ] = useState(() => searchParams.get('q') ?? '');
  // Fonte de campanha (utm_source) escolhida no resumo; '-' = sem campanha.
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A busca global do header navega para /tenants?q=… — refletir aqui.
  const externalQ = searchParams.get('q');
  useEffect(() => {
    if (externalQ !== null) {
      // Sincroniza o campo local com a URL: a busca do header navega para
      // /tenants?q=…, e sem isto a caixa continuaria mostrando o termo antigo.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQ(externalQ);
      setPage(1);
    }
  }, [externalQ]);

  useEffect(() => {
    let cancelled = false;
    // Mesmo padrao de busca de dados das demais telas; ver FeatureFlagDetailPage.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    api
      .get<Page<TenantListItem>>('/platform/tenants', {
        params: {
          subscriptionStatus: subscriptionStatus || undefined,
          isActive: isActive === '' ? undefined : isActive,
          q: q || undefined,
          source: source || undefined,
          page,
          pageSize: 20,
        },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data.items);
        setTotalPages(data.totalPages);
        setTotalItems(data.totalItems);
      })
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [page, subscriptionStatus, isActive, q, source]);

  return (
    <div className="page">
      <AcquisitionSummaryCard onPickSource={(picked) => { setSource(picked); setPage(1); }} />

      <div className="card" style={{ padding: 16 }}>
        <div className="grid-3" style={{ alignItems: 'end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="q">Buscar por nome, slug ou telefone</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 11,
                  color: 'var(--color-text-tertiary)',
                  display: 'inline-flex',
                  pointerEvents: 'none',
                }}
              >
                <Search size={16} />
              </span>
              <input
                id="q"
                value={q}
                placeholder="Ex.: cantina, burger-do-ze…"
                style={{ width: '100%', paddingLeft: 34 }}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
              />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="status">Assinatura</label>
            <select
              id="status"
              value={subscriptionStatus}
              onChange={(e) => { setSubscriptionStatus(e.target.value); setPage(1); }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="active">Ativo</label>
            <select
              id="active"
              value={isActive}
              onChange={(e) => { setIsActive(e.target.value as 'true' | 'false' | ''); setPage(1); }}
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
        </div>
        {source && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge tone="info">Origem: {sourceFilterLabel(source)}</Badge>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setSource(''); setPage(1); }}
              aria-label="Remover filtro de origem"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card card-flush">
        <div className="table-scroll">
          <CardTable aria-label="Lojas">
            <THead>
              <Tr>
                <Th>Restaurante</Th>
                <Th>Cardápio</Th>
                <Th>Status</Th>
                <Th>Ativo</Th>
                <Th>Teste grátis expira</Th>
                <Th>Origem</Th>
                <Th></Th>
              </Tr>
            </THead>
            <TBody>
              {loading && (
                <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Carregando…</Td></Tr>
              )}
              {!loading && items.length === 0 && (
                <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Nenhuma loja encontrada.</Td></Tr>
              )}
              {!loading && items.map((t) => {
                const sub = subscriptionBadge(t.subscriptionStatus);
                return (
                  <Tr key={t.id}>
                    <Td heading>
                      <div style={{ fontWeight: 600 }}>{t.tradeName}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        <span className="mono" style={{ fontSize: 12 }}>{t.slug}</span> · {t.ownerName}
                      </div>
                    </Td>
                    {/* Ícone + copiar em vez da URL inteira: a linha da tabela
                        não comporta o endereço sem empurrar as demais colunas. */}
                    <Td label="Cardápio">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <a
                          href={t.menuUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                          aria-label={`Abrir cardápio de ${t.tradeName}`}
                          title={t.menuUrl}
                        >
                          <ExternalLink size={15} />
                        </a>
                        <CopyButton
                          value={t.menuUrl}
                          label={`Copiar link do cardápio de ${t.tradeName}`}
                          iconOnly
                        />
                      </div>
                    </Td>
                    <Td label="Status"><Badge tone={sub.tone} dot>{sub.label}</Badge></Td>
                    <Td label="Ativo">
                      {t.isActive
                        ? <Badge tone="success">Sim</Badge>
                        : <Badge tone="error">Não</Badge>}
                    </Td>
                    <Td label="Teste grátis expira" className="muted">
                      {t.trialExpiresAt ? new Date(t.trialExpiresAt).toLocaleDateString('pt-BR') : '—'}
                    </Td>
                    <Td label="Origem">
                      {t.acquisition?.source ? (
                        <>
                          <div>{t.acquisition.source}</div>
                          {t.acquisition.campaign && (
                            <div className="muted mono" style={{ fontSize: 12 }}>{t.acquisition.campaign}</div>
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </Td>
                    <Td action style={{ textAlign: 'right' }}>
                      <Link to={`/tenants/${t.id}`} style={{ fontWeight: 600 }}>Detalhe →</Link>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      </div>

      <div className="pagination">
        <span className="pagination-info">
          {totalItems} {totalItems === 1 ? 'loja' : 'lojas'} · página {page} de {totalPages}
        </span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
        >Anterior</button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
        >Próxima</button>
      </div>
    </div>
  );
}
